import { createTRPCUntypedClient, httpLink } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import type { ExportStatus, JobAccepted } from '@charitystack/org-engagement-contracts';

export type ExportHeader = Readonly<{ label: string; key: string }>;
export type StartExportInput = Readonly<{
  entityType:
    | 'contacts'
    | 'subscriptions'
    | 'payouts'
    | 'payouts-drilldown'
    | 'payouts-report'
    | 'transactions'
    | 'forms'
    | 'elements'
    | 'fundraiser-drilldown'
    | 'event-drilldown'
    | 'broadcasts'
    | 'segments';
  filters: Record<string, unknown>;
  headers: readonly ExportHeader[];
  selectedIds?: readonly string[];
  excludedIds?: readonly string[];
  userEmail?: string;
}>;
export type StartTaxDocumentsInput = Readonly<{
  year: unknown;
  filters: Record<string, unknown>;
  selectedIds?: readonly string[];
  excludedIds?: readonly string[];
  userEmail?: string;
  /** CON-829: one paid currency; omitted for All (the job then covers every currency). */
  currency?: string;
}>;

export type TypedNotificationsResponse<T> = Omit<Response, 'json'> & { json(): Promise<T> };

export interface NotificationsDashboardFacade {
  startExport(
    input: StartExportInput,
    authorization: string,
  ): Promise<TypedNotificationsResponse<JobAccepted>>;
  exportStatus(
    jobId: string,
    authorization: string,
  ): Promise<TypedNotificationsResponse<ExportStatus>>;
  startTaxDocuments(
    input: StartTaxDocumentsInput,
    authorization: string,
  ): Promise<TypedNotificationsResponse<JobAccepted>>;
  startEmailTaxDocuments(
    input: StartTaxDocumentsInput,
    authorization: string,
  ): Promise<TypedNotificationsResponse<JobAccepted>>;
  deleteNotification(
    id: string,
    authorization: string,
  ): Promise<TypedNotificationsResponse<unknown>>;
  markNotificationRead(
    id: string,
    authorization: string,
  ): Promise<TypedNotificationsResponse<unknown>>;
  markAllNotificationsRead(authorization: string): Promise<TypedNotificationsResponse<unknown>>;
}

function legacyUrl(path: string): string {
  return `${withoutTrailingSlash(import.meta.env.VITE_APP_NOTIFICATION_URL)}/${path}`;
}

function legacyRequest<T>(
  path: string,
  authorization: string,
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH',
  body?: unknown,
): Promise<TypedNotificationsResponse<T>> {
  return globalThis.fetch(legacyUrl(path), {
    method,
    headers: {
      Authorization: authorization,
      ...(body !== undefined || method === 'GET' ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }) as Promise<TypedNotificationsResponse<T>>;
}

/** Sole normal application binding: legacy REST only, with no candidate selection or fallback. */
export const notificationsClient = Object.freeze<NotificationsDashboardFacade>({
  startExport: (input, authorization) =>
    legacyRequest('export/start', authorization, 'POST', input),
  exportStatus: (jobId, authorization) =>
    legacyRequest(`export/status/${jobId}`, authorization, 'GET'),
  startTaxDocuments: (input, authorization) =>
    legacyRequest('tax-documents/start', authorization, 'POST', input),
  startEmailTaxDocuments: (input, authorization) =>
    legacyRequest('email-tax-documents/start', authorization, 'POST', input),
  deleteNotification: (id, authorization) =>
    legacyRequest(`notifications/${id}`, authorization, 'DELETE'),
  markNotificationRead: (id, authorization) =>
    legacyRequest(`notifications/${id}/read`, authorization, 'PATCH'),
  markAllNotificationsRead: (authorization) =>
    legacyRequest('notifications/read-all', authorization, 'PATCH'),
});

/** Explicit test-only dark transport. Construction performs no request and has no default endpoint. */
export function createNotificationsTrpcTransport({
  endpoint,
  request,
}: Readonly<{ endpoint: string; request: typeof globalThis.fetch }>): NotificationsDashboardFacade {
  const normalizedEndpoint = withoutTrailingSlash(endpoint);
  if (!normalizedEndpoint) throw new Error('A Notifications tRPC endpoint is required');
  const client = createTRPCUntypedClient<AnyRouter>({
    links: [httpLink({ url: normalizedEndpoint, fetch: request })],
  });

  return Object.freeze<NotificationsDashboardFacade>({
    async startExport(input) {
      return typedBody<JobAccepted>(await client.mutation('export.start', input));
    },
    async exportStatus(jobId) {
      return typedBody<ExportStatus>(await client.query('export.status', { jobId }));
    },
    async startTaxDocuments(input) {
      return typedBody<JobAccepted>(await client.mutation('tax-documents.start', input));
    },
    async startEmailTaxDocuments(input) {
      return typedBody<JobAccepted>(await client.mutation('email-tax-documents.start', input));
    },
    async deleteNotification(id) {
      return typedBody(await client.mutation('notifications.delete', { id }));
    },
    async markNotificationRead(id) {
      return typedBody(await client.mutation('notifications.mark-read', { id }));
    },
    async markAllNotificationsRead() {
      return typedBody(await client.mutation('notifications.mark-all-read', {}));
    },
  });
}

function typedBody<T>(body: unknown): TypedNotificationsResponse<T> {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'content-type': 'application/json' },
  }) as TypedNotificationsResponse<T>;
}

function withoutTrailingSlash(value: string | undefined): string {
  return (value ?? '').replace(/\/$/, '');
}
